import type { JSX } from "preact";
import {
  useBcaGeometrySheetMode,
  useBcaSheetAutoSwitch,
} from "../hooks/useBcaSheetMode";
import { useAccModelStore } from "../stores/accModelStore";
import { useViewerStore } from "../stores/viewerStore";

export function SheetPicker() {
  const viewerUrn = useAccModelStore(s => s.viewerUrn);
  const apsViewables = useAccModelStore(s => s.viewables);
  const currentViewableId = useAccModelStore(s => s.currentViewableId);
  const setViewable = useAccModelStore(s => s.setViewable);

  const { bcaMode, visibleSheets } = useBcaGeometrySheetMode();
  const currentSheetId = useViewerStore(s => s.currentSheetId);
  const setCurrentSheet = useViewerStore(s => s.setCurrentSheet);

  useBcaSheetAutoSwitch();

  const isAps = !!viewerUrn && apsViewables.length > 0;

  const onChange = (e: JSX.TargetedEvent<HTMLSelectElement, Event>) => {
    const value = e.currentTarget.value;
    if (isAps) setViewable(value);
    else setCurrentSheet(value);
  };

  const selectValue = isAps ? (currentViewableId ?? "") : currentSheetId;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        width: "100%",
      }}
    >
      {bcaMode && (
        <div
          role="note"
          style={{
            fontSize: 10,
            lineHeight: 1.45,
            color: "#e5b85f",
            background: "rgba(229,184,95,0.12)",
            border: "1px solid rgba(229,184,95,0.35)",
            borderRadius: 4,
            padding: "6px 8px",
          }}
        >
          BCA checks use 3D ray-marching — only the 3D building model is shown.
        </div>
      )}
      {isAps && (
        <div
          role="note"
          style={{
            fontSize: 10,
            lineHeight: 1.45,
            color: "#7fb3d5",
            background: "rgba(127,179,213,0.12)",
            border: "1px solid rgba(127,179,213,0.35)",
            borderRadius: 4,
            padding: "6px 8px",
          }}
        >
          Live ACC model — all 2D sheets + 3D. Rule checks read properties from
          the full 3D model; sheets are for viewing and highlighting.
        </div>
      )}
      <label
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          fontSize: 11,
          color: "var(--fc2-text-dim)",
          fontWeight: 600,
        }}
      >
        Sheet
        <select
          value={selectValue}
          onChange={onChange}
          disabled={isAps && apsViewables.length === 0}
          style={{
            width: "100%",
            background: "var(--fc2-bg)",
            color: "var(--fc2-text)",
            border: "1px solid var(--fc2-border)",
            borderRadius: 4,
            padding: "6px 8px",
            fontSize: 12,
            fontWeight: 400,
          }}
        >
          {isAps
            ? apsViewables.map(v => (
                <option key={v.id} value={v.id}>
                  {v.name}
                  {v.kind === "3d" && !bcaMode ? "  (3D)" : ""}
                </option>
              ))
            : visibleSheets.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {s.kind === "3d" && !bcaMode ? "  (3D)" : ""}
                </option>
              ))}
        </select>
      </label>
    </div>
  );
}
